// ** Third Party Components
import { parseCookies } from "nookies"
import { Outlet } from "react-router-dom"
import { useEffect, useState } from "react"

// ** Variables
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { FINANCIAL_SERVER_URL, ORGANIZATION_PROJECT_ROLES_ENDPOINT } from "@pma/utils/api-routes"

// ** Hooks
import { usePermissions } from "@pma/hooks/use-permissions"

// ** Components
import PmsLoader from "@components/common/loader"
import FinancialAccessDenied from "../components/financials/financial-access-denied"

type FetchState = "idle" | "loading" | "success" | "error"

const FinancialGuard = () => {
  const { isOrgAdmin } = usePermissions()

  const [fetchState, setFetchState] = useState<FetchState>("idle")
  const [hasRoles, setHasRoles] = useState<boolean>(false)

  useEffect(() => {
    // Admins bypass the financial role check entirely
    if (isOrgAdmin) return

    const token = parseCookies()[ACCESS_TOKEN]

    const fetchRoles = async () => {
      setFetchState("loading")
      try {
        const response = await fetch(
          FINANCIAL_SERVER_URL + ORGANIZATION_PROJECT_ROLES_ENDPOINT,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        if (!response.ok) {
          setFetchState("error")
          return
        }

        const data: unknown[] = await response.json()
        setHasRoles(Array.isArray(data) && data.length > 0)
        setFetchState("success")
      } catch {
        setFetchState("error")
      }
    }

    fetchRoles()
  }, [isOrgAdmin])

  // Admins always get through
  if (isOrgAdmin) {
    return <Outlet />
  }

  if (fetchState === "idle" || fetchState === "loading") {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <PmsLoader className="min-h-[50vh]" />
      </div>
    )
  }

  if (fetchState === "error" || !hasRoles) {
    return <FinancialAccessDenied />
  }

  return <Outlet />
}

export default FinancialGuard
